import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Button, Card, Modal, Spin, Tooltip } from "antd";
import { fetchDetails } from "../../redux/data/dataAction";
import { LoadingOutlined } from "@ant-design/icons";
import PDFViewer from "./PDFViewer";

const Search = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const [pdfTitle, setPdfTitle] = useState("");
  const [clickReset, setClickReset] = useState(false);
  const [resetTrigger, setResetTrigger] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchNode, setSearchNode] = useState([]);
  const [searchVal, setSearchVal] = useState("");
  const [openResponsive, setOpenResponsive] = useState(false);
  const sasUrls = useSelector((state) => state.data.sasUrls);
  const metadata = useSelector((state) => state.data.data);
  const searchResults = useSelector((state) => state.data.searchResults);
  const navigate = useNavigate();
  useEffect(() => {
    if (!location?.state?.searchValue) {
      return;
    }
    handleSearch(location.state.searchValue);
  }, [location.state]);

  const handleFilterSearch = (data) => {
    const paths = data.map((item) => item.path);
    paths.sort((a, b) => a.split("/").length - b.split("/").length);
    const parentPaths = [];
    paths.forEach((path) => {
      if (!parentPaths.some((parent) => path.startsWith(parent + "/"))) {
        parentPaths.push(path);
      }
    });
    const parentDocuments = data
      .filter((item) => parentPaths.includes(item.path))
      .filter((doc) => {
        return !(
          doc?.path?.startsWith("capabilities/Certification") &&
          doc?.path?.endsWith(".pdf")
        );
      });
    setSearchNode(parentDocuments);
  };
  const handleReset = () => {
    setResetTrigger((prev) => !prev);
  };
  const handleSearch = async (search) => {
    setLoading(true);
    try {
      setSearchVal(search);

      const action = fetchDetails(search);
      const result = await dispatch(action);

      handleFilterSearch(result.payload.searchResults);
    } catch (error) {
      setLoading(false);
      console.error("Error during search:", error);
    }
    setLoading(false);
  };
  const isFile = (node) => {
    [(".pdf", ".pptx", ".docx", ".xlsx", ".doc")].some((ext) => {
      node?.path?.endsWith(ext);
    });
  };

  const handleCardClick = (nodeData) => {
    setLoading(true);

    let node = nodeData.path;

    const filterString = node;
    const clickedFile = metadata.find(
      (item) => item.path && item.path.endsWith(filterString)
    );
    if (node.endsWith(".pdf")) {
      setPdfTitle(nodeData?.name_of_document);
      if (clickedFile && node.endsWith(".pdf")) {
        const matchedSasUrl = sasUrls.find(
          (item) => item.blob_path === clickedFile.path
        );

        if (matchedSasUrl) {
          setOpenResponsive(true);
          setSelectedFile(matchedSasUrl.sas_url);
        }
        setLoading(false);
        return;
      }
      setPdfTitle(node);
    } else if (clickedFile?.link !== undefined) {
      window.open(clickedFile?.link, "_blank");
      setLoading(false);
      return;
    } else {
      if (node?.startsWith("capabilities/Certification")) {
        const fullNodePath = node;
        const pathLength = fullNodePath.split("/").length;

        const filesInCertification = metadata.filter((data) => {
          return (
            data.path.startsWith(`${fullNodePath}/`) &&
            data.path.split("/").length === pathLength + 1
          );
        });
        const hasPdfInNextPath = filesInCertification.some((data) =>
          data.path.toLowerCase().endsWith(".pdf")
        );

        if (hasPdfInNextPath) {
          console.log(
            "Navigation prevented because a .pdf file exists directly under this folder."
          );
          setLoading(false);
          return;
        } else {
          navigate(`/node/${node}`);
          setLoading(false);
        }
        setLoading(false);
        return;
      } else {
        setLoading(false);
        navigate(`/node/${node}`);
      }
    }
    setLoading(false);
  };

  const isBlog = (nodeData) => {
    return nodeData && nodeData.path.startsWith("blogs/");
  };
  const isClient = (nodeData) => {
    return nodeData && nodeData.path.startsWith("client/");
  };

  const shortenSummary = (summary) => {
    if (!summary) return "";
    const maxLength = 60;
    if (summary.length > maxLength) {
      return summary.substring(0, maxLength) + "...";
    }
    return summary;
  };

  return (
    <div>
      <div
        className="mt-[30px] ml-[30px]"
        style={{
          fontSize: "14px",
        }}
      >
        Showing Result(s):
        <span style={{ fontWeight: "800", fontSize: "16px" }}>
          {" "}
          {searchVal}
        </span>
      </div>

      {loading ? (
        <Spin
          indicator={
            <LoadingOutlined style={{ fontSize: 48, color: "#005BA1" }} spin />
          }
          tip="Loading"
          fullscreen
        />
      ) : searchNode.length === 0 ? (
        <div className="flex justify-center items-center h-64">
          <p className="text-gray-500">No results found</p>
        </div>
      ) : (
        <div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 px-16 py-4">
            {searchNode.map((doc, index) => {
              const matchedSasUrl = sasUrls.find(
                (item) => item.blob_path === doc.iconimage
              );
              const imageUrl = matchedSasUrl ? matchedSasUrl.sas_url : null;

              return isClient(doc) ? (
                <div key={doc}>
                  {isFile(doc) ? (
                    <Card
                      hoverable
                      variant="borderless"
                      className="text-center h-52 flex flex-col items-center justify-center custom-card"
                      styles={{
                        body: {
                          justifyItems: "center",
                        },
                      }}
                      onClick={() => handleCardClick(doc)}
                    >
                      <div className="w-20 h-20 flex items-center justify-center mb-2 ">
                        {imageUrl && (
                          <img src={imageUrl} className="object-contain" />
                        )}
                      </div>

                      <h3 className="text-lg font-medium text-gray-700">
                        {doc.name_of_document}
                      </h3>

                      {doc && (
                        <div className="node-details">
                          <Tooltip
                            title={doc.summary}
                            placement="bottom"
                            color="#555555"
                          >
                            <p style={{ fontStyle: "60%" }}>
                              {shortenSummary(doc.summary)}
                            </p>
                          </Tooltip>
                          {isFile(doc) && <></>}
                        </div>
                      )}
                    </Card>
                  ) : (
                    <Card
                      hoverable
                      variant="borderless"
                      className="text-center h-52 flex flex-col items-center justify-center custom-card"
                      styles={{
                        body: {
                          justifyItems: "center",
                        },
                      }}
                      onClick={() => handleCardClick(doc)}
                    >
                      <div className="w-44 h-44 flex items-center justify-center mb-2">
                        {imageUrl && (
                          <img src={imageUrl} className="object-contain" />
                        )}
                      </div>

                      {isFile(doc) && (
                        <>
                          <h3 className="text-lg font-medium text-gray-700">
                            {doc.name_of_document}
                          </h3>
                        </>
                      )}
                      {doc && (
                        <div className="node-details">
                          <Tooltip
                            title={doc.summary}
                            placement="bottom"
                            color="#555555"
                          >
                            <p style={{ fontStyle: "60%" }}>
                              {shortenSummary(doc.summary)}
                            </p>
                          </Tooltip>
                          {isFile(doc) && <></>}
                        </div>
                      )}
                    </Card>
                  )}
                </div>
              ) : (
                doc?.name_of_document && (
                  <div key={index}>
                    <Card
                      hoverable
                      variant="borderless"
                      className="text-center h-52 flex flex-col items-center justify-center custom-card"
                      styles={{
                        body: {
                          justifyItems: "center",
                        },
                      }}
                      onClick={() => handleCardClick(doc)}
                    >
                      {imageUrl && (
                        <div className="w-[7rem] h-auto flex items-center justify-center mb-2 ">
                          <img src={imageUrl} className="h-14 object-contain" />
                        </div>
                      )}
                      <h3 className="text-lg font-medium text-gray-700 mt-4">
                        {doc?.name_of_document}
                      </h3>
                      {doc && (
                        <div className="node-details">
                          {doc.path.startsWith("capabilities/Certification") ? (
                            <h3
                              style={{ fontStyle: "60%" }}
                              className="text-sm font-medium text-gray-700"
                            >
                              {/* {shortenSummary(doc.summary)} */}
                            </h3>
                          ) : (
                            <Tooltip
                              title={doc.summary}
                              placement="bottom"
                              color="#555555"
                            >
                              <p style={{ fontStyle: "60%" }}>
                                {shortenSummary(doc.summary)}
                              </p>
                            </Tooltip>
                          )}
                          {isFile(doc) && <></>}
                        </div>
                      )}
                    </Card>
                  </div>
                )
              );
            })}
          </div>
        </div>
      )}
      <Modal
        title={pdfTitle}
        width={"100vw"}
        style={{
          top: 0,
          height: "100vh",
          padding: 0,
        }}
        styles={{
          body: {
            height: "calc(100vh - 110px)",
            overflow: "hidden",
          },
        }}
        open={openResponsive}
        onOk={() => setOpenResponsive(false)}
        onCancel={() => {
          handleReset();
          setOpenResponsive(false);
        }}
        footer={[
          <Button
            className="modal-button"
            key="reset"
            onClick={handleReset}
            style={{ color: "#005BA1", borderColor: "#005BA1" }}
          >
            Reset View
          </Button>,
          <Button
            className="modal-button"
            key="close"
            onClick={() => {
              handleReset();
              setOpenResponsive(false);
            }}
            style={{ color: "red", borderColor: "red" }}
          >
            Close
          </Button>,
        ]}
      >
        <div className="py-3">
          <div
            className="pdf-viewer-container"
            style={{
              width: "100%",
              height: "calc(100vh - 160px)",
            }}
          >
            <PDFViewer pdfBlobUrl={selectedFile} resetTrigger={resetTrigger} />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Search;
